import React, { useEffect, useId, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { STATIC_PLANS } from '../../data/staticPlans';
import { usePricingData } from '../../hooks/usePricingData';
import { AppleProduct, requestProducts } from '../../services/appleIAPBridge';
import { PlanConfig } from '../../services/subscriptionService';
import { MINIMUM_TOP_UP_CREDITS } from '../../utils/creditGate';
import { getCustomerPlanLabel } from '../../utils/planDisplay';
import { isIOSApp } from '../../utils/platformDetection';
import { resolvePricingCurrency } from '../../utils/pricingCountry';
import { getMinimumTopUpCredits, getQuickTopUpAmounts } from '../../utils/topUpPricing';

import './CreditTopUpCard.css';
import {
  SecurePaymentNote,
  TopUpForm,
  getEstimatedPrice
} from './CreditTopUpComponents';
import type { TopUpFormLayout } from './CreditTopUpComponents';

interface CreditTopUpCardProps {
  onTopUp: (amount: number, currency?: string) => void;
  isProcessing: boolean;
  onAppleTopUp?: (productId: string) => void | Promise<void>;
  initialCreditAmount?: number;
  fallbackPlanId?: string;
  secondaryOffer?: React.ReactNode | ((pricing: CreditTopUpPricingContext) => React.ReactNode);
  /** The currency to price in; by default the profile's choice, else the country's. */
  currency?: BillingCurrency;
  /** 'split' puts the total and Buy button in a summary beside the amount (for a wide dialog). */
  layout?: TopUpFormLayout;
}

type BillingCurrency = 'USD' | 'INR';

export interface CreditTopUpPricingContext {
  plans: Record<string, PlanConfig>;
  currentPlanId: string;
  currency: BillingCurrency;
  loading: boolean;
}

/**
 * The preselected amount never starts below the minimum top-up, so a caller
 * that passes a raw shortfall (a fraction of a credit) does not open the form
 * on a purchase the user would have to repeat immediately. The field itself
 * stays free — this only chooses where it starts.
 */
const normalizeInitialCreditAmount = (amount?: number): number => (
  typeof amount === 'number' && Number.isFinite(amount) && amount > 0
    ? Math.max(MINIMUM_TOP_UP_CREDITS, Math.ceil(amount))
    : MINIMUM_TOP_UP_CREDITS
);

const APPLE_CREDIT_PRODUCT_IDS = [
  'com.app.ai8080.credits.USD5',
  'com.app.ai8080.credits.USD10',
  'com.app.ai8080.credits.USD20',
  'com.app.ai8080.credits.USD50',
];

/**
 * Apple IAP credit buttons — shown on iOS instead of custom amount input.
 * Displays Apple's localized pricing with tier-specific credit counts.
 */
const AppleCreditButtons: React.FC<{
  onAppleTopUp: (productId: string) => void | Promise<void>;
  isProcessing: boolean;
  tierCreditsMap?: Record<string, number>;
}> = ({ onAppleTopUp, isProcessing, tierCreditsMap }) => {
  const [appleProducts, setAppleProducts] = useState<AppleProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await requestProducts(APPLE_CREDIT_PRODUCT_IDS);

        // Sort by price ascending
        products.sort((a, b) => a.price - b.price);
        setAppleProducts(products);
      } catch (error) {
        console.error('[CreditTopUp] Failed to fetch Apple products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  if (loadingProducts) {
    return (
      <div className="apple-credits-loading">
        <span className="btn-spinner" /> Loading prices...
      </div>
    );
  }

  if (appleProducts.length === 0) {
    return (
      <div className="apple-credits-error">
        Unable to load Apple pricing. Please try again later.
      </div>
    );
  }

  const handleClick = async (productId: string) => {
    setPurchasingId(productId);
    try {
      await onAppleTopUp(productId);
    } finally {
      setPurchasingId(null);
    }
  };

  return (
    <div className="apple-credit-buttons">
      {appleProducts.map((product) => {
        const credits = tierCreditsMap?.[product.productId] || 0;
        const isBuying = purchasingId === product.productId;

        return (
          <button
            key={product.productId}
            className={`apple-credit-btn${isBuying ? ' apple-credit-btn--purchasing' : ''}`}
            onClick={() => handleClick(product.productId)}
            disabled={isProcessing}
          >
            {isBuying ? (
              <>
                <span className="btn-spinner" />
                <span className="apple-credit-amount">Processing…</span>
              </>
            ) : (
              <>
                <span className="apple-credit-price">{product.localizedPrice}</span>
                {credits > 0 && (
                  <span className="apple-credit-amount">{credits} credits</span>
                )}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};

export const CreditTopUpCard: React.FC<CreditTopUpCardProps> = ({
  onTopUp,
  isProcessing,
  onAppleTopUp,
  initialCreditAmount,
  fallbackPlanId,
  secondaryOffer,
  currency: controlledCurrency,
  layout = 'stacked',
}) => {
  const { user } = useAuth();
  const { plans, currentSub, loading, billingCountry } = usePricingData();
  const [creditAmount, setCreditAmount] = useState<number>(() => (
    normalizeInitialCreditAmount(initialCreditAmount)
  ));
  const inputId = useId();
  const onIOS = isIOSApp();
  const currency = controlledCurrency
    ?? resolvePricingCurrency(user?.preferred_currency, user?.country || billingCountry, onIOS);
  const currentPlanId = currentSub?.plan_id || fallbackPlanId || user?.subscription_tier || 'free';
  const currentPlanLabel = getCustomerPlanLabel(currentPlanId);
  const renderedSecondaryOffer = typeof secondaryOffer === 'function'
    ? secondaryOffer({ plans, currentPlanId, currency, loading })
    : secondaryOffer;

  // Calculate tier-specific credits for each Apple product
  const tierCreditsMap = React.useMemo(() => {
    if (loading || !plans) return {};
    const tier = currentPlanId;
    const plan = plans[tier] || plans.free;
    const topupPrice = plan?.topup_price_per_credit_apple || plan?.topup_price_per_credit
      || STATIC_PLANS.free.topup_price_per_credit_apple || 0;

    const map: Record<string, number> = {};
    const dollarValues: Record<string, number> = {
      'com.app.ai8080.credits.USD5': 5,
      'com.app.ai8080.credits.USD10': 10,
      'com.app.ai8080.credits.USD20': 20,
      'com.app.ai8080.credits.USD50': 50,
    };

    for (const [productId, dollarValue] of Object.entries(dollarValues)) {
      map[productId] = topupPrice > 0 ? Math.floor(dollarValue / topupPrice) : 0;
    }

    return map;
  }, [currentPlanId, loading, plans]);

  const estimatedPrice = getEstimatedPrice(creditAmount, currency, loading, plans, currentPlanId);
  const minimumCredits = getMinimumTopUpCredits(plans, currentPlanId);

  return (
    <div className="credit-top-up-card">
      <div className="top-up-content">
        {onIOS && onAppleTopUp ? (
          /* iOS: Show fixed Apple IAP buttons */
          <>
            <h3 className="apple-topup-title">Buy Credits</h3>
            <p className="apple-topup-subtitle">
              Credits based on your <strong>{currentPlanLabel}</strong> plan rate
            </p>
            <AppleCreditButtons
              onAppleTopUp={onAppleTopUp}
              isProcessing={isProcessing}
              tierCreditsMap={tierCreditsMap}
            />
            <SecurePaymentNote text="Secure payment via Apple In-App Purchase." />
          </>
        ) : (
          /* Web/Android: Standard Stripe/Razorpay flow */
          <TopUpForm
            layout={layout}
            creditAmount={creditAmount}
            setCreditAmount={setCreditAmount}
            isProcessing={isProcessing}
            estimatedPrice={estimatedPrice}
            inputId={inputId}
            minimumCredits={minimumCredits}
            quickAmounts={getQuickTopUpAmounts(minimumCredits)}
            paymentNote={`Secure payment processing via ${currency === 'INR' ? 'Razorpay' : 'Stripe'}.`}
            onBuy={() => creditAmount >= minimumCredits && onTopUp(creditAmount, currency)}
            priceFor={(amount) => getEstimatedPrice(amount, currency, loading, plans, currentPlanId)}
          />
        )}
        {!onIOS && renderedSecondaryOffer}
      </div>
    </div>
  );
};
